// ──────────────────────────────────────────────────────────────────────
// Publish Guardrails — Phase 2E (frontend-only, non-blocking)
// Returns an array of guardrail objects for the given event form.
// All checks use fields already present on the event document; no
// backend changes. Any guardrail returning a card is shown in the
// unified "Before You Publish" modal.
// ──────────────────────────────────────────────────────────────────────

function isBlank(s) {
  return !s || typeof s !== 'string' || !s.trim();
}

function toNumber(v) {
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : 0;
}

export function getPublishGuardrails(event) {
  if (!event || typeof event !== 'object') return [];

  const guardrails = [];
  const tiers = Array.isArray(event.pass_tiers) ? event.pass_tiers : [];
  const entryTiers = tiers.filter((t) => (t?.type || 'entry') !== 'addon');

  // 1. All tiers free — only fires when >1 entry tier AND every price === 0
  if (entryTiers.length > 1 && entryTiers.every((t) => toNumber(t.price) === 0)) {
    guardrails.push({
      id: 'all-tiers-free',
      severity: 'warning',
      title: 'All tiers are free',
      message: 'Every entry tier is currently set to $0. Confirm this is intentional before publishing.',
      actionLabel: 'Review pricing',
      anchor: 'tiers',
      signature: `all-tiers-free|tiers=${entryTiers.length}|allFree=true`,
    });
  }

  // 2. Missing description
  if (isBlank(event.description)) {
    const len = (event.description || '').trim().length;
    guardrails.push({
      id: 'missing-description',
      severity: 'warning',
      title: 'Description missing',
      message: 'A short description helps guests understand the event before requesting access or getting tickets.',
      actionLabel: 'Add description',
      anchor: 'description',
      signature: `missing-description|len=${len}`,
    });
  }

  // 3. Hidden location (info-level — legitimate setting)
  if (event.location_visibility === 'hidden' || event.hidden_location === true) {
    guardrails.push({
      id: 'hidden-location',
      severity: 'info',
      title: 'Location is hidden',
      message: 'Guests may not see the full location until you reveal it. Confirm this is intentional.',
      actionLabel: 'Review location',
      anchor: 'location',
      signature: `hidden-location|hidden=true`,
    });
  }

  // 4. No capacity limit — fires when event-level unlimited OR every entry tier is unlimited
  const eventUnlimited = event.is_unlimited_capacity === true || (!event.capacity || toNumber(event.capacity) === 0);
  const allEntryUnlimited = entryTiers.length > 0 && entryTiers.every((t) => t.is_unlimited === true);
  if (eventUnlimited || allEntryUnlimited) {
    const cap = toNumber(event.capacity);
    guardrails.push({
      id: 'no-capacity',
      severity: 'info',
      title: 'No capacity limit',
      message: 'This event may accept unlimited passes unless you set capacity limits.',
      actionLabel: 'Review capacity',
      anchor: 'tiers',
      signature: `no-capacity|capacity=${cap}|unlimited=${!!eventUnlimited || !!allEntryUnlimited}`,
    });
  }

  // 5. Paid tiers but no payment method configured
  const paidCount = entryTiers.filter((t) => toNumber(t.price) > 0).length;
  const paymentLinks = Array.isArray(event.payment_links) ? event.payment_links : [];
  if (paidCount > 0 && paymentLinks.length === 0) {
    guardrails.push({
      id: 'paid-no-payment',
      severity: 'warning',
      title: 'Payment details may be missing',
      message: 'Paid events should clearly show guests how to submit payment.',
      actionLabel: 'Review payment',
      anchor: 'payment',
      signature: `paid-no-payment|paidCount=${paidCount}|hasLinks=false`,
    });
  }

  // Phase 2F: warnings before info (stable sort within same severity)
  const severityWeight = (s) => (s === 'warning' ? 0 : 1);
  return guardrails
    .map((g, i) => ({ g, i }))
    .sort((a, b) => {
      const sd = severityWeight(a.g.severity) - severityWeight(b.g.severity);
      return sd !== 0 ? sd : a.i - b.i;
    })
    .map((x) => x.g);
}
